import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { fetchSessions, deleteSession, supabase } from '../services/supabaseService';
import { updateSessionAdmin, deleteSessionAdmin } from '../services/adminService';
import { announceToScreenReader, focusElement } from '../utils/accessibility';
import { Search, Edit, Trash2, Download, Filter, RefreshCw, X, Check, LogOut, BarChart3, MessageSquare, Link, Settings, Home } from 'lucide-react';
import AdminAuth from '../components/admin/AdminAuth';
import AdminDashboard from '../components/admin/AdminDashboard';
import QuestionManager from '../components/admin/QuestionManager';
import UrlModerator from '../components/admin/UrlModerator';
import ContentModerator from '../components/admin/ContentModerator';
import Analytics from '../components/admin/Analytics';
import UserManager from '../components/admin/UserManager';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    day: 'all',
    track: 'all',
    hasAbstract: 'all',
    sessionLevel: 'all'
  });
  const [sortConfig, setSortConfig] = useState({ key: 'track', direction: 'asc' });
  const [showFilters, setShowFilters] = useState(false);

  // Get active tab from URL hash
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;

      // Handle both formats: #announcements and #tab=announcements
      let tabName = null;
      
      // First try tab=value format
      const tabMatch = hash.match(/tab=([^&]+)/);
      if (tabMatch) {
        tabName = tabMatch[1];
      } else if (hash.startsWith('#')) {
        // Handle direct hash format like #announcements
        const directTab = hash.substring(1);
        if (['announcements', 'sessions', 'questions', 'sponsors', 'dashboard'].includes(directTab)) {
          tabName = directTab;
        }
      }
      
      if (tabName) {
        setActiveTab(tabName);
      }
    };

    handleHashChange(); // Check initial hash
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [activeTab]);

  // Check authentication status on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          const isAdmin = await checkAdminStatus(session.user.email);

          if (isAdmin) {
            setIsAuthenticated(true);
            setCurrentUser(session.user);
          } else {
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('❌ Admin: Authentication check failed:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  }, []);



  const checkAdminStatus = async (email) => {
    try {
      const { data, error } = await supabase.rpc('is_admin', { user_email: email });
      if (error) {
        console.error('❌ Admin: is_admin RPC failed:', error.message);
        return false;
      }
      return data === true;
    } catch (error) {
      console.error('❌ Admin: Exception in checkAdminStatus:', error);
      return false;
    }
  };

  const handleLogin = async (credentials) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password
      });

      if (error) {
        console.error('❌ Admin: Login failed:', error.message);
        return { success: false, error: error.message };
      }

      if (data.user) {
        const isAdmin = await checkAdminStatus(data.user.email);

        if (isAdmin) {
          setIsAuthenticated(true);
          setCurrentUser(data.user);
          announceToScreenReader('Admin login successful');
          return { success: true };
        } else {
          await supabase.auth.signOut();
          return { success: false, error: 'Access denied: Admin privileges required' };
        }
      }

      return { success: false, error: 'Login failed' };
    } catch (error) {
      console.error('❌ Admin: Exception during login:', error);
      return { success: false, error: error.message };
    }
  };

  const handleLogout = async () => {
    try {
      // Clear state first to prevent UI issues
      setIsAuthenticated(false);
      setCurrentUser(null);
      setSessions([]);
      setFilteredSessions([]);

      // Then sign out from Supabase
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('❌ Admin: Logout error:', error);
        // Don't throw error, just log it
      }

      announceToScreenReader('Logged out successfully');
    } catch (error) {
      console.error('❌ Admin: Exception during logout:', error);
      // Still clear the state even if logout fails
      setIsAuthenticated(false);
      setCurrentUser(null);
      setSessions([]);
      setFilteredSessions([]);
    }
  };

  // Load sessions from Supabase
  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const data = await fetchSessions();

      setSessions(data);
    } catch (err) {
      setError('Failed to load sessions: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Apply filters and search
  const applyFilters = useCallback(() => {
    let filtered = [...sessions];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(session =>
        session.title?.toLowerCase().includes(term) ||
        session.speaker?.toLowerCase().includes(term) ||
        (session.abstract_content || session.abstract)?.toLowerCase().includes(term) ||
        session.location?.toLowerCase().includes(term)
      );
    }

    // Day filter
    if (filters.day !== 'all') {
      filtered = filtered.filter(session => session.date === filters.day);
    }

    // Track filter
    if (filters.track !== 'all') {
      filtered = filtered.filter(session => session.track === filters.track);
    }

    // Abstract filter
    if (filters.hasAbstract !== 'all') {
      const hasAbstract = filters.hasAbstract === 'true';
      filtered = filtered.filter(session => Boolean(session.hasAbstract) === hasAbstract);
    }

    // Session level filter
    if (filters.sessionLevel !== 'all') {
      filtered = filtered.filter(session => session.sessionLevel === filters.sessionLevel);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';

      if (sortConfig.direction === 'asc') {
        return aVal.toString().localeCompare(bVal.toString());
      } else {
        return bVal.toString().localeCompare(aVal.toString());
      }
    });

    setFilteredSessions(filtered);
  }, [sessions, searchTerm, filters, sortConfig]);

  const handleUpdateSession = async (sessionId, updates) => {
    try {
      const updatedSession = await updateSessionAdmin(sessionId, updates);

      if (updatedSession) {
        setSessions(prev => prev.map(session =>
          session.id === sessionId ? { ...session, ...updatedSession } : session
        ));
      } else {
        console.warn('⚠️ [ADMIN UI] No session data returned from update');
      }
    } catch (err) {
      console.error('❌ [ADMIN UI] Failed to update session:', err);
      setError('Failed to update session: ' + err.message);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this session?')) {
      return;
    }

    try {
      await deleteSessionAdmin(sessionId);

      setSessions(prev => prev.filter(session => session.id !== sessionId));
      setSelectedSessions(prev => prev.filter(id => id !== sessionId));
    } catch (err) {
      console.error('❌ [ADMIN UI] Failed to delete session:', err);
      setError('Failed to delete session: ' + err.message);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedSessions.length === 0) {
      setError('Please select sessions first');
      return;
    }

    if (action === 'delete') {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Are you sure you want to delete ${selectedSessions.length} sessions?`)) {
        return;
      }

      try {
        for (const sessionId of selectedSessions) {
          await deleteSessionAdmin(sessionId);
        }
        setSessions(prev => prev.filter(session => !selectedSessions.includes(session.id)));
        setSelectedSessions([]);
      } catch (err) {
        console.error('❌ [ADMIN UI] Failed to bulk delete sessions:', err);
        setError('Failed to delete sessions: ' + err.message);
      }
    }
  };

  // Handle session selection
  const toggleSessionSelection = (sessionId) => {
    setSelectedSessions(prev => {
      if (prev.includes(sessionId)) {
        return prev.filter(id => id !== sessionId);
      } else {
        return [...prev, sessionId];
      }
    });
  };

  const selectAllSessions = () => {
    if (selectedSessions.length === filteredSessions.length) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(filteredSessions.map(session => session.id));
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Export sessions to JSON
  const exportSessions = () => {
    const dataStr = JSON.stringify(filteredSessions, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sessions-export-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    announceToScreenReader(`Exported ${filteredSessions.length} sessions`);
  };

  // Load sessions on component mount
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Apply filters when dependencies change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Focus management
  useEffect(() => {
    const heading = document.querySelector('h1');
    if (heading) {
      focusElement(heading);
    }
  }, []);

  // Screen reader announcements
  const announceToScreenReaderLocal = (message) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  };

  // Show loading spinner while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <AdminAuth onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading sessions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">Error</p>
            <p>{typeof error === 'object' && error.message ? error.message : error}</p>
          </div>
          <button
            onClick={loadSessions}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 inline mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'sessions', label: 'Sessions', icon: Settings },
    { id: 'questions', label: 'Q&A', icon: MessageSquare },
    { id: 'urls', label: 'Presenter URLs', icon: Link },
    { id: 'content', label: 'Content', icon: Edit },
    { id: 'users', label: 'Users', icon: Settings },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 }
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    window.location.hash = `#/admin?tab=${tabId}`;
    announceToScreenReaderLocal(`Switched to ${tabs.find(t => t.id === tabId)?.label} tab`);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <AdminDashboard user={currentUser} />;
      case 'sessions':
        return renderSessionsTab();
      case 'questions':
        return <QuestionManager user={currentUser} />;
      case 'urls':
        return <UrlModerator user={currentUser} />;
      case 'content':
        return <ContentModerator user={currentUser} />;
      case 'announcements':
        return <ContentModerator user={currentUser} activeTab="announcements" />;
      case 'users':
        return <UserManager user={currentUser} />;
      case 'analytics':
        return <Analytics user={currentUser} />;
      default:
        return <AdminDashboard user={currentUser} />;
    }
  };

  const renderSessionsTab = () => {
    return (
      <div>
        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          {/* Search and Actions Row */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search sessions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2 rounded-md border transition-colors ${
                  showFilters
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4 inline mr-2" />
                Filters
              </button>

              <button
                onClick={exportSessions}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4 inline mr-2" />
                Export
              </button>

              <button
                onClick={loadSessions}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4 inline mr-2" />
                Refresh
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="border-t pt-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label htmlFor="filter-day" className="block text-sm font-medium text-gray-700 mb-1">Day</label>
                  <select
                    id="filter-day"
                    value={filters.day}
                    onChange={(e) => setFilters(prev => ({ ...prev, day: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Days</option>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="filter-track" className="block text-sm font-medium text-gray-700 mb-1">Track</label>
                  <select
                    id="filter-track"
                    value={filters.track}
                    onChange={(e) => setFilters(prev => ({ ...prev, track: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Tracks</option>
                    <option value="1.1">Track 1.1</option>
                    <option value="1.2">Track 1.2</option>
                    <option value="2.1">Track 2.1</option>
                    <option value="2.2">Track 2.2</option>
                    <option value="3.1">Track 3.1</option>
                    <option value="3.2">Track 3.2</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="filter-abstract" className="block text-sm font-medium text-gray-700 mb-1">Abstract</label>
                  <select
                    id="filter-abstract"
                    value={filters.hasAbstract}
                    onChange={(e) => setFilters(prev => ({ ...prev, hasAbstract: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Sessions</option>
                    <option value="true">With Abstract</option>
                    <option value="false">Without Abstract</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="filter-level" className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                  <select
                    id="filter-level"
                    value={filters.sessionLevel}
                    onChange={(e) => setFilters(prev => ({ ...prev, sessionLevel: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Levels</option>
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Actions */}
          {selectedSessions.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  {selectedSessions.length} session{selectedSessions.length !== 1 ? 's' : ''} selected
                </span>
                <select
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Choose action...</option>
                  <option value="delete">Delete Selected</option>
                  <option value="export">Export Selected</option>
                </select>
                <button
                  onClick={() => handleBulkAction(bulkAction)}
                  disabled={!bulkAction}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sessions Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedSessions.length === filteredSessions.length && filteredSessions.length > 0}
                      onChange={selectAllSessions}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  {[
                    { key: 'track', label: 'Track' },
                    { key: 'title', label: 'Title' },
                    { key: 'speaker', label: 'Speaker' },
                    { key: 'date', label: 'Day' },
                    { key: 'time', label: 'Time' },
                    { key: 'hasAbstract', label: 'Abstract' }
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort(key)}
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        {sortConfig.key === key && (
                          <span className="text-blue-600">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSessions.map((session) => (
                  <SessionRow
                    key={session.id}
                    session={session}
                    isSelected={selectedSessions.includes(session.id)}
                    onToggleSelect={toggleSessionSelection}
                    onEdit={() => {}}
                    onDelete={handleDeleteSession}
                    onUpdate={handleUpdateSession}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {filteredSessions.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No sessions found matching your criteria.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Manage conference content, sessions, and user interactions</p>
            {currentUser && (
              <p className="text-sm text-gray-500 mt-1">
                Logged in as: <span className="font-medium">{currentUser.email}</span>
              </p>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Admin tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`${
                      isActive
                        ? 'border-blue-500 text-blue-600 bg-white'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 bg-white'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors`}
                    aria-current={isActive ? 'page' : undefined}
                    style={{ backgroundColor: 'white', color: isActive ? '#2563eb' : '#6b7280' }}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

// Session Row Component
const SessionRow = ({ session, isSelected, onToggleSelect, onEdit: _onEdit, onDelete, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  const handleEdit = () => {
    setEditData({
      title: session.title || '',
      speaker: session.speaker || '',
      abstract_content: session.abstract_content || session.abstract || '',
      location: session.location || '',
      session_level: session.session_level || session.viewDetails?.level || '',
      topic_category: session.topic_category || session.topic || ''
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      await onUpdate(session.id, editData);
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving session:', err);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({});
  };

  if (isEditing) {
    return (
      <tr className="bg-blue-50">
        <td className="px-6 py-4">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
        </td>
        <td className="px-6 py-4 text-sm text-gray-900">{session.track}</td>
        <td className="px-6 py-4">
          <input
            type="text"
            value={editData.title}
            onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
          />
        </td>
        <td className="px-6 py-4">
          <input
            type="text"
            value={editData.speaker}
            onChange={(e) => setEditData(prev => ({ ...prev, speaker: e.target.value }))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
          />
        </td>
        <td className="px-6 py-4 text-sm text-gray-900">{session.date}</td>
        <td className="px-6 py-4 text-sm text-gray-900">{session.time}</td>
        <td className="px-6 py-4">
          <textarea
            value={editData.abstract_content}
            onChange={(e) => setEditData(prev => ({ ...prev, abstract_content: e.target.value }))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            rows="2"
            placeholder="Abstract content"
          />
        </td>
        <td className="px-6 py-4">
          <input
            type="text"
            value={editData.session_level}
            onChange={(e) => setEditData(prev => ({ ...prev, session_level: e.target.value }))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            placeholder="Session level"
          />
        </td>
        <td className="px-6 py-4">
          <input
            type="text"
            value={editData.topic_category}
            onChange={(e) => setEditData(prev => ({ ...prev, topic_category: e.target.value }))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            placeholder="Topic category"
          />
        </td>
        <td className="px-6 py-4">
          <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
            session.hasAbstract ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {session.hasAbstract ? 'Yes' : 'No'}
          </span>
        </td>
        <td className="px-6 py-4">
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="text-green-600 hover:text-green-900 transition-colors"
              title="Save changes"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={handleCancel}
              className="text-gray-600 hover:text-gray-900 transition-colors"
              title="Cancel editing"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
        {session.track}
      </td>
      <td className="px-6 py-4 text-sm text-gray-900">
        <div className="max-w-xs truncate" title={session.title}>
          {session.title}
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-900">
        <div className="max-w-xs truncate" title={session.speaker}>
          {session.speaker}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {session.date}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {session.time}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
          session.hasAbstract ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {session.hasAbstract ? 'Yes' : 'No'}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex gap-2">
          <button
            onClick={handleEdit}
            className="text-blue-600 hover:text-blue-900 transition-colors"
            title="Edit session"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="text-red-600 hover:text-red-900 transition-colors"
            title="Delete session"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// PropTypes for SessionRow component
SessionRow.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string,
    speaker: PropTypes.string,
    abstract: PropTypes.string,
    abstract_content: PropTypes.string,
    location: PropTypes.string,
    session_level: PropTypes.string,
    topic_category: PropTypes.string,
    track: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    date: PropTypes.string,
    time: PropTypes.string,
    hasAbstract: PropTypes.bool
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired
};

export default Admin;
